import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Form, Alert } from 'react-bootstrap';
import { FilePdf, X } from 'react-bootstrap-icons';
import { apiRequest } from '../utils/api';
import { getActiveCompanyId } from '../utils/companyContext';

const defaultForm = {
  name: '',
  designation: '',
  phone: '',
  crmSource: '',
};

const ResumeUploadForm = ({ show, onHide, designations = [], onUploaded, companyId = getActiveCompanyId() }) => {
  const [formState, setFormState] = useState(defaultForm);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [designationOptions, setDesignationOptions] = useState([]);
  const [designationError, setDesignationError] = useState('');
  const [isLoadingDesignations, setIsLoadingDesignations] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!show) {
      setFormState(defaultForm);
      setSelectedFile(null);
      setError('');
      setIsSubmitting(false);
      setDesignationError('');
      return;
    }

    let isMounted = true;

    const fetchDesignations = async () => {
      try {
        setIsLoadingDesignations(true);
        setDesignationError('');
        if (!companyId) {
          setDesignationOptions([]);
          return;
        }
        const params = new URLSearchParams({ companyId: String(companyId) });
        const { data } = await apiRequest(`/api/admin/settings/designations?${params.toString()}`);
        if (!isMounted) {
          return;
        }
        const fetchedDesignations = Array.isArray(data) ? data : [];
        setDesignationOptions(fetchedDesignations);
      } catch (designationFetchError) {
        if (isMounted) {
          console.error('Failed to fetch designations:', designationFetchError);
          setDesignationError(
            designationFetchError.message || 'Unable to load designations. Please try again.'
          );
        }
      } finally {
        if (isMounted) {
          setIsLoadingDesignations(false);
        }
      }
    };

    fetchDesignations();

    return () => {
      isMounted = false;
    };
  }, [companyId, show]);

  useEffect(() => {
    if (!show) {
      return;
    }

    if (!Array.isArray(designations) || designations.length === 0) {
      return;
    }

    setDesignationOptions(designations);
  }, [designations, show]);

  const sortedDesignations = useMemo(() => {
    if (!Array.isArray(designationOptions)) {
      return [];
    }

    return [...designationOptions].sort((a, b) => a.designation.localeCompare(b.designation));
  }, [designationOptions]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileSelect = (event) => {
    const [file] = event.target.files ?? [];
    setSelectedFile(file || null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formState.name.trim()) {
      setError('Candidate name is required.');
      return;
    }

    if (!selectedFile) {
      setError('Please select a resume file (PDF).');
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');

      const formData = new FormData();
      formData.append('name', formState.name.trim());
      formData.append('designation', formState.designation);
      formData.append('phone', formState.phone);
      formData.append('crmSource', formState.crmSource);
      formData.append('file', selectedFile);

      const { data } = await apiRequest('/api/admin/resumes', {
        method: 'POST',
        body: formData,
      });

      if (typeof onUploaded === 'function') {
        onUploaded(data);
      }

      onHide();
    } catch (uploadError) {
      setError(uploadError.message || 'Failed to upload resume.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Upload Resume</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" className="py-2">
              {error}
            </Alert>
          )}
          <Form.Group className="mb-3" controlId="resumeName">
            <Form.Label>
              Candidate Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formState.name}
              onChange={handleInputChange}
              placeholder="Enter candidate name"
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="resumeCrm">
            <Form.Label>CRM Source</Form.Label>
            <Form.Control
              type="text"
              name="crmSource"
              value={formState.crmSource}
              onChange={handleInputChange}
              placeholder="Enter CRM reference"
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="resumeFile">
            <Form.Label>
              Resume File (PDF) <span className="text-danger">*</span>
            </Form.Label>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => fileInputRef.current?.click()}
              >
                Choose File
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              className="d-none"
              onChange={handleFileSelect}
              accept="application/pdf"
            />
            {selectedFile && (
              <div className="mt-2 p-2 bg-light rounded d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <FilePdf className="text-danger me-2" />
                  <span className="small">{selectedFile.name}</span>
                </div>
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => {
                    setSelectedFile(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                >
                  <X />
                </button>
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-3" controlId="resumeDesignation">
            <Form.Label>Designation</Form.Label>
            <Form.Select
              name="designation"
              value={formState.designation}
              onChange={handleInputChange}
              disabled={isLoadingDesignations}
            >
              <option value="">Select Designation</option>
              {sortedDesignations.map((designation) => (
                <option key={designation.id} value={designation.designation}>
                  {designation.designation}
                </option>
              ))}
            </Form.Select>
            {designationError && (
              <Form.Text className="text-danger">{designationError}</Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-0" controlId="resumePhone">
            <Form.Label>Phone Number</Form.Label>
            <Form.Control
              type="tel"
              name="phone"
              value={formState.phone}
              onChange={handleInputChange}
              placeholder="Enter contact number"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onHide}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-danger" disabled={isSubmitting}>
            {isSubmitting ? 'Uploading…' : 'Upload'}
          </button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ResumeUploadForm;